import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { ColDef, IsFullWidthRowParams } from "ag-grid-community";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { IndividualEntityDividerComponent } from "../individual-entity-divider/individual-entity-divider.component";
import { GridOptions, RowHeightParams } from "ag-grid-enterprise";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ContactsService } from "@shared/services/contacts.service";
import { RelationshipsApiService } from "@shared/services/relationships-api.service";
import { IndividualDto } from "@models/individuals/individual-dto";
import { RelationshipIndividualDto } from "@models/individuals/relationship-individual-dto";
import { IContactsChangeEvent } from "@pages/individuals/components/individuals-page/components/contacts/contacts.component";
import { Contact } from "@models/common/contact";
import {
  IEditContactModalResult
} from "@shared/components/contacts/edit-contact-modal/edit-contact-modal.component";
import { BulkUpdateContactDto } from "@models/billing-signing/bulk-update-contact-dto";
import {
  getContactsColDefs,
  getContactsOverlayNoRowsTemplate
} from "@pages/individuals/components/individuals-page/components/contacts/utils/get-contacts-col-defs";

@Component({
  selector: 'app-change-relationship-contact-details-modal',
  standalone: true,
  imports: [
    CommonModalWrapperComponent,
    LoaderComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'change-relationship-contact-details-modal.component.html',
})
export class ChangeRelationshipContactDetailsModalComponent implements OnInit {
  private contactsService = inject(ContactsService);
  private contactsApiService = inject(ContactsApiService);
  private relationshipsService = inject(RelationshipsApiService);
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  @Input() individual!: IndividualDto;
  @Input() relationshipsToUpdate: RelationshipIndividualDto[] = [];

  @Output() _contactsChanged = new EventEmitter<IContactsChangeEvent>();

  readonly fullWidthCellRenderer = IndividualEntityDividerComponent;

  selectedContacts: Contact[] = [];

  isLoading = signal<boolean>(false);
  viewContacts = signal<(Contact | string)[]>([]);

  ngOnInit(): void {
    this.fillViewContacts(this.individual);
  }

  onAddContact(): void {
    const { instance, result } = this.contactsService.openEditContactModal();

    instance.individual = this.individual!;

    result.then(
      (result: IEditContactModalResult) => {
        if (result.isPrimaryContact && this.individual) {
          this.individual.primaryContactId = result.contact.contactId;
          this.individual.primaryContact = result.contact;
        }

        this.loadContacts();
      },
      () => {
      }
    );
  }

  onSelectContacts(contacts: Contact[]) {
    this.selectedContacts = contacts.filter((contact) => typeof contact !== 'string');
  }

  submit(): void {
    if (!this.selectedContacts.length) return;

    this.isLoading.set(true);

    const payload: BulkUpdateContactDto = {
      contactId: this.selectedContacts[0].contactId,
      individualId: this.individual.individualId,
      relationshipsIdToUpdate: this.relationshipsToUpdate.map((relationship) => relationship.relationshipId)
    };

    this.relationshipsService.setRelationshipsContacts(payload)
      .subscribe({
        next: () => this.activeModal.close(),
        error: (error) => {
          this.isLoading.set(false);
          console.warn('[ChangeRelationshipContactDetailsModal.submit] error: ', error);
          this.toastr.error('Failed to update contacts', 'Error');
        }
      });

  }

  close(): void {
    this.activeModal.dismiss();
  }

  loadContacts(): void {
    if (!this.individual) return;

    this.isLoading.set(true);
    this.contactsApiService.getIndividualContacts(this.individual.individualId)
      .subscribe({
        next: (contacts) => {
          this.individual.contacts = contacts;
          this.fillViewContacts(this.individual);
          this._contactsChanged.emit({ contacts, primaryContact: this.individual?.primaryContact ?? null });
          this.isLoading.set(false);
        },
        error: (error) => {
          console.warn('[ContactsComponent.loadContacts] error: ', error);
          this.toastr.error('Failed to load individual contacts', 'Error');
        }
      });
  }

  fillViewContacts(individual: IndividualDto): void {
    const contacts: (Contact | string)[] = [];

    if (!individual.primaryContact) {
      this.viewContacts.set(individual.contacts);
      this.setColDefs();
      this.isLoading.set(false);
      return;
    }

    const otherContacts = individual.contacts
      .filter((contact) => contact.contactId !== individual.primaryContactId);

    contacts.push('Primary Contact');
    contacts.push(individual.primaryContact);

    if (individual.contacts.length > 1) {
      contacts.push('Other Contacts');
      contacts.push(...otherContacts);
    }

    this.setColDefs();
    this.viewContacts.set(contacts);
    this.isLoading.set(false);
  }


  // ===== ===== ===== Table Columns config and other ===== ===== =====


  readonly gridOptions: GridOptions = { overlayNoRowsTemplate: getContactsOverlayNoRowsTemplate() };

  isFullWidthRow(params: IsFullWidthRowParams<unknown>): boolean {
    return typeof params.rowNode.data == 'string';
  }

  getRowHeight: (params: RowHeightParams) => number | undefined | null = (
    params: RowHeightParams,
  ) => {
    return typeof params.data == 'string'
      ? 24
      : 56;
  };

  setColDefs(): void {
    this.colDefs = getContactsColDefs(true);
  }

  colDefs: ColDef[] = getContactsColDefs(true);

  get contactsChangedEvent(): Observable<IContactsChangeEvent> {
    return this._contactsChanged
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef));
  }
}
